import { RequestKind, boolean, string } from '@sb/library/base';
import { BatchEventUpdateItem, CalendarBirthdayEventDisplayItem, CalendarBirthdayEventSearchOptions, CalendarBookingDisplayItem, CalendarBookingSearchOptions, CalendarConflictCheckSearchOptions, CalendarEventConvertInputItem, CalendarEventDisplayItem, CalendarEventInputItem, CalendarEventSearchOptions, CalendarEventUpdateItem, CalendarHoldDisplayItem, CalendarHoldSearchOptions, CalendarLocationUnavailableDisplayItem, CalendarLocationUnavailableSearchOptions, CalendarRepertoirePerformanceDisplayItem, CalendarSyncURLInputItem, CalendarTeacherAvailabilityDisplayItem, CalendarTeacherAvailabilitySearchOptions, CalendarUpdateEventCheckSearchOptions, CalendarUpdateEventRequirementsDisplayItem, DisplayItemCollection, EventDeleteOptions, ExternalCalendarFeedDisplayItem, ExternalCalendarFeedInputItem, ExternalCalendarFeedSearchOptions, ExternalCalendarFeedUpdateItem, ExternalEventDisplayItem, ExternalEventSearchOptions, ICalendarConflictDisplayItem } from '@sb/library/data-models';
import { ApiRoute } from '../api-route';

export namespace CalendarRoutes {
    export namespace Events {
        export const Add = ApiRoute.Define({
            URL: `calendar/event`,
            Kind: RequestKind.Post,
            BodyType: CalendarEventInputItem,
            ResultType: Array<string>
        });

        export const Update = ApiRoute.Define({
            URL: (eventID: string) => `calendar/events/${eventID}`,
            Kind: RequestKind.Put,
            BodyType: CalendarEventUpdateItem,
            ResultType: boolean
        });

        export const Batch = ApiRoute.Define({
            URL: `calendar/event`,
            Kind: RequestKind.Patch,
            BodyType: BatchEventUpdateItem,
            ResultType: boolean
        });

        export const Delete = ApiRoute.Define({
            URL: (eventID: string) => `calendar/event/${eventID}`,
            Kind: RequestKind.Delete,
            BodyType: EventDeleteOptions,
            ResultType: boolean
        });

        export const GetBirthday = ApiRoute.Define({
            URL: `search/calendar/birthdays`,
            PagingAllowed: true,
            Kind: RequestKind.Post,
            BodyType: CalendarBirthdayEventSearchOptions,
            ResultType: DisplayItemCollection<CalendarBirthdayEventDisplayItem>
        });

        export const Get = ApiRoute.Define({
            URL: (eventID: string) => `calendar/events/${eventID}`,
            Kind: RequestKind.Get,
            ResultType: CalendarEventDisplayItem
        });

        export const GetAll = ApiRoute.Define({
            URL: `calendar/events`,
            Kind: RequestKind.Get,
            PagingAllowed: true,
            ResultType: DisplayItemCollection<CalendarEventDisplayItem>
        });

        export const Search = ApiRoute.Define({
            URL: `search/calendar/events`,
            PagingAllowed: true,
            Kind: RequestKind.Post,
            BodyType: CalendarEventSearchOptions,
            ResultType: DisplayItemCollection<CalendarEventDisplayItem>
        });

        export const GetRepertoire = ApiRoute.Define({
            URL: `search/calendar/repertoire`,
            PagingAllowed: true,
            Kind: RequestKind.Post,
            BodyType: CalendarEventSearchOptions,
            ResultType: DisplayItemCollection<CalendarRepertoirePerformanceDisplayItem>
        });

        export const GetLocationUnavailable = ApiRoute.Define({
            URL: `search/calendar/unavailabilities/location`,
            PagingAllowed: true,
            Kind: RequestKind.Post,
            BodyType: CalendarLocationUnavailableSearchOptions,
            ResultType: DisplayItemCollection<CalendarLocationUnavailableDisplayItem>
        });

        export const GetTeacherAvailability = ApiRoute.Define({
            URL: `search/calendar/availabilities/teacher`,
            PagingAllowed: true,
            Kind: RequestKind.Post,
            BodyType: CalendarTeacherAvailabilitySearchOptions,
            ResultType: DisplayItemCollection<CalendarTeacherAvailabilityDisplayItem>
        });

        export const GetUpdateRequirements = ApiRoute.Define({
            URL: (eventID: string) => `calendar/events/${eventID}/updaterequirements`,
            Kind: RequestKind.Post,
            BodyType: CalendarUpdateEventCheckSearchOptions,
            ResultType: CalendarUpdateEventRequirementsDisplayItem
        });

        export namespace Hold {
            export const Search = ApiRoute.Define({
                URL: 'search/calendar/holds',
                Kind: RequestKind.Post,
                BodyType: CalendarHoldSearchOptions,
                ResultType: DisplayItemCollection<CalendarHoldDisplayItem>
            });
        }

        export namespace External {
            export const Search = ApiRoute.Define({
                URL: 'search/calendar/externalevents',
                Kind: RequestKind.Post,
                BodyType: ExternalEventSearchOptions,
                ResultType: DisplayItemCollection<ExternalEventDisplayItem>
            });
        }
    }

    export namespace ExternalFeeds {
        export const GetAll = ApiRoute.Define({
            URL: `externalcalendarfeeds`,
            Kind: RequestKind.Get,
            PagingAllowed: true,
            ResultType: DisplayItemCollection<ExternalCalendarFeedDisplayItem>
        });

        export const Add = ApiRoute.Define({
            URL: (teacherID : string) => `teachers/${teacherID}/externalcalendarfeeds/`,
            Kind: RequestKind.Post,
            BodyType: ExternalCalendarFeedInputItem,
            ResultType: string
        });

        export const Delete = ApiRoute.Define({
            URL: (feedID : string) => `externalcalendarfeeds/${feedID}`,
            Kind: RequestKind.Delete,
            ResultType: boolean
        });

        export const Update = ApiRoute.Define({
            URL: (feedID : string) => `externalcalendarfeeds/${feedID}`,
            Kind: RequestKind.Put,
            BodyType: ExternalCalendarFeedUpdateItem,
            ResultType: boolean
        });

        export const Refresh = ApiRoute.Define({
            URL: (teacherID : string, externalFeedID: string) => `teachers/${teacherID}/externalcalendarfeeds/${externalFeedID}/refresh`,
            Kind: RequestKind.Post,
            ResultType: boolean
        });

        export const Search = ApiRoute.Define({
            URL: 'search/externalcalendarfeeds',
            Kind: RequestKind.Post,
            BodyType: ExternalCalendarFeedSearchOptions,
            PagingAllowed: true,
            ResultType: DisplayItemCollection<ExternalCalendarFeedDisplayItem>
        });
    }

    export const ConvertToMakeUp = ApiRoute.Define({
        URL: (eventID: string) => `calendar/event/${eventID}/convert`,
        Kind: RequestKind.Post,
        BodyType: CalendarEventConvertInputItem,
        ResultType: string
    });

    export const GetConflicts = ApiRoute.Define({
        URL: 'search/calendar/conflicts',
        Kind: RequestKind.Post,
        BodyType: CalendarConflictCheckSearchOptions,
        ResultType: Array<ICalendarConflictDisplayItem>
    });

    export const GenerateSyncURL = ApiRoute.Define({
        URL: `calendar/sync`,
        Kind: RequestKind.Post,
        BodyType: CalendarSyncURLInputItem,
        ResultType: string
    });

    export namespace BookingSlots {
        export const GetAll = ApiRoute.Define({
            URL: `search/calendar/bookingslots`,
            Kind: RequestKind.Post,
            PagingAllowed: true,
            BodyType: CalendarBookingSearchOptions,
            OrderByType: CalendarBookingDisplayItem,
            ExtraParameters: 'TimeZone',
            ResultType: DisplayItemCollection<CalendarBookingDisplayItem>
        });

        export const Get = ApiRoute.Define({
            URL: (schoolID: string, websiteBlockID: string) => `schools/${schoolID}/websiteblocks/${websiteBlockID}/bookingslots`,
            Kind: RequestKind.Post,
            PagingAllowed: true,
            BodyType: CalendarBookingSearchOptions,
            OrderByType: CalendarBookingDisplayItem,
            ExtraParameters: 'TimeZone',
            ResultType: DisplayItemCollection<CalendarBookingDisplayItem>,
        });
    }
}
